"use client";

import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Paper,
  FormControl,
} from "@mui/material";
import { validateEmail } from "@/utils/validation";
import { removeEmojiHtmlTag } from "@/utils/sanitizeInput";
import { useAlert } from "@/contexts/AlertContext";
import { apiRemote } from "@/api";

// Custom X icon since we can't use lucide-react
const XIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom Paperclip icon
const PaperclipIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface OpenCertDialogProps {
  open: boolean;
  onClose: () => void;
  initialData?: any;
  reload: () => void;
}

export default function OpenCertDialog({ open, onClose, initialData, reload }: OpenCertDialogProps) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    file: null as File | null,
  });
  
  const alert = useAlert();

  const resetFormData = () => {
    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      file: null,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.file) {
        alert.error("Please attach a file.");
        return;
    }

    if (Object.values(errors).some((error) => error)) {
      alert.error("Please fix the errors before submitting.");
      return;
    }

    // console.log(formData);
    apiRemote.sendRequestCert({
        billing_first_name: formData.firstName,
        billing_last_name: formData.lastName,
        billing_phone: formData.phone,
        billing_email: formData.email,
        moduleId: initialData?.module_id,
        cerName: initialData?.cer_name,
        cerLink: initialData?.cer_link,
        proofOfPayment: formData.file,
    }).then((res)=>{
        if(res.code == 200 || res.code == 201){
            alert.success("Request sent successfully");
            reload();
            handleClose();
        }else{
            alert.error(res.message);
        }
        
    }).catch((err)=>{
        alert.error(err.message);
        throw err;
    });
  };

  const transferInfo = {
    bankAccountName: "Creative Educare (M) Sdn Bhd",
    bankName: "Maybank",
    bankAccountNo: "512307104739",
    amount: "RM 15",
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let sanitizedValue = value;

    if (name === "email") {
      const emailError = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: removeEmojiHtmlTag(sanitizedValue),
    }));
  };

  const handleInputFileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let sanitizedValue: string | File | null = value;

    if (name === "file") {
      const inputElement = e.target as HTMLInputElement;
      const file1 = inputElement.files ? inputElement.files[0] : null;
      sanitizedValue = file1; // Gán file1 vào sanitizedValue
    }
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };
  const handleClose = () => {
    resetFormData();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        style={{
          margin: 0,
          padding: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Request for OpenCert
        <IconButton
          onClick={handleClose}
          style={{
            color: "#666",
          }}
        >
          <XIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: 24 }}>
        <form onSubmit={handleSubmit}>
          <Box style={{ marginBottom: 24 }}>
            <Box
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <FormControl fullWidth margin="normal">
                <Typography variant="body1" color="textSecondary">
                  First name:{" "}
                  <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl fullWidth margin="normal">
                <Typography variant="body1" color="textSecondary">
                  Last name:{" "}
                  <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>
            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Phone:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <TextField
                required
                fullWidth
                name="phone"
                style={{ marginBottom: 16 }}
                value={formData.phone}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Typography variant="body1" color="textSecondary">
                Email address:{" "}
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
              </Typography>
              <TextField
                required
                fullWidth
                type="email"
                name="email"
                value={formData.email}
                error={!!errors.email}
                helperText={errors.email}
                onChange={handleChange}
              />
            </FormControl>
          </Box>

          <Paper
            variant="outlined"
            style={{
              padding: 16,
              marginBottom: 24,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              THE TRANSFER INFORMATION
            </Typography>
            <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Typography variant="body2">
                - Bank account name:{" "}
                <Typography component="span" color="error">
                  {transferInfo.bankAccountName}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Bank name:{" "}
                <Typography component="span" color="error">
                  {transferInfo.bankName}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Bank account no.:{" "}
                <Typography component="span" color="error">
                  {transferInfo.bankAccountNo}
                </Typography>
              </Typography>
              <Typography variant="body2">
                - Amount payable:{" "}
                <Typography component="span" color="error">
                  {transferInfo.amount}
                </Typography>
              </Typography>
            </Box>
          </Paper>
          <Box
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 24,
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <label
              htmlFor="file-upload"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                border: "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: 4,
                cursor: "pointer",
                backgroundColor: "#f5f5f5",
              }}
            >
              <PaperclipIcon />
              <Typography style={{ textTransform: "none" }}>Attach</Typography>
              <input
                id="file-upload"
                style={{ display: "none" }}
                type="file"
                name="file"
                accept=".jpeg,.jpg,.png"
                onChange={handleInputFileChange}
              />
            </label>
            {formData.file && (
                <Typography variant="body2" style={{ color: "#555" }}>
                    Attached file: <strong>{formData.file.name}</strong>
                </Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            style={{
              backgroundColor: "rgb(15, 105, 185)",
              textTransform: "none",
            }}
          >
            Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
